import {
  CustomFieldType,
  customFieldValueSchemaByType,
} from "@repo/app-types/entities/customField";
import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import { Searcher } from "fast-fuzzy";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import compact from "lodash-es/compact.js";
import memoize from "lodash-es/memoize.js";
import orderBy from "lodash-es/orderBy.js";

export function extractNameParts(rawValues: {
  firstName: string | undefined;
  lastName: string | undefined;
  fullName: string | undefined;
}): { firstName: string | undefined; lastName: string | undefined } {
  const values = {
    firstName: rawValues.firstName?.trim(),
    lastName: rawValues.lastName?.trim(),
    fullName: rawValues.fullName?.trim(),
  };

  // If a first name and last name are provided, use them.
  if (values.firstName && values.lastName) {
    return {
      firstName: values.firstName,
      lastName: values.lastName,
    };
  }

  if (values.firstName && values.fullName?.startsWith(values.firstName)) {
    const lastName = values.fullName.substring(values.firstName.length).trim();
    return { firstName: values.firstName, lastName };
  }

  if (values.lastName && values.fullName?.endsWith(values.lastName)) {
    const firstName = values.fullName
      .substring(0, values.fullName.length - values.lastName.length)
      .trim();
    return { firstName, lastName: values.lastName };
  }

  const commaSplit = values.fullName?.split(",").map((name) => name.trim());
  if (commaSplit?.length === 2) {
    const [lastName, firstName] = commaSplit;
    return { firstName, lastName };
  }

  const spaceSpace = values.fullName?.split(" ", 2).map((name) => name.trim());
  if (spaceSpace?.length === 2) {
    const [firstName, lastName] = spaceSpace;
    return { firstName, lastName };
  }

  return {
    firstName: values.firstName || values.fullName,
    lastName: values.lastName,
  };
}

type ProjectSearchFunction = (
  queries: readonly string[],
) => ProjectWithInterviewCount | undefined;

/** Creates a function to perform a fuzzy search against projects with caching and other optimizations. */
export function createProjectSearcher(
  projects: readonly ProjectWithInterviewCount[],
): ProjectSearchFunction {
  const searcher = new Searcher([...projects], {
    // TODO: Tune this default threshold.
    // threshold: 0.6,
    keySelector: (project) =>
      compact([
        // Match against the project name.
        project.name,
        // Match against the job type name and location combo, if applicable.
        project.job_type && project.location
          ? `${project.job_type.name} - ${project.location.description}`
          : undefined,
      ]),
  });

  // Memoize search queries, since a CSV file is likely going to have many repeat values.
  const search = memoize((query: string) =>
    searcher.search(query, {
      returnMatchData: true,
    }),
  );

  return (queries) => {
    // Early return if no search queries were provided.
    if (queries.length === 0) {
      return undefined;
    }

    // Find the match with the highest score or greatest length.
    const matches = queries.flatMap(search);
    const sortedMatches = orderBy(
      matches,
      [(match) => match.score, (match) => match.match.length],
      ["desc", "desc"],
    );
    const bestMatch = sortedMatches.at(0);
    return bestMatch?.item;
  };
}

export function extractProject(
  projects: readonly ProjectWithInterviewCount[],
  searchProjects: ProjectSearchFunction,
  values: {
    id: string | undefined;
    name: string | undefined;
    role: string | undefined;
    location: string | undefined;
  },
): ProjectWithInterviewCount | undefined {
  // If an ID was provided, use that.
  if (values.id) {
    return projects.find((project) => project.id === values.id);
  }

  // If any of these fields were provided, perform a fuzzy search.
  if (values.name || values.role || values.location) {
    const searchQueries = [];

    // A job opening name was provided.
    if (values.name) {
      searchQueries.push(values.name);
    }

    // A role and location combination were provided.
    if (values.role && values.location) {
      searchQueries.push(`${values.role} ${values.location}`);
    }

    return searchProjects(searchQueries);
  }

  return undefined;
}

export function extractEmailAddress(
  email: string | undefined,
): string | undefined {
  // Bare minimum validation.
  if (!email?.includes("@")) {
    return undefined;
  }

  // Bare minimum normalization.
  return email.trim().toLowerCase();
}

export function extractPhoneNumber(
  phoneNumber: string | undefined,
): string | undefined {
  if (!phoneNumber) {
    return undefined;
  }

  // Note: We don't check if the phone number is possible or valid, because that's too strict and would require our libphonenumber metadata to be up to date at all times.
  return parsePhoneNumberFromString(phoneNumber, "US")?.formatInternational();
}

export function extractCustomField(
  fieldType: CustomFieldType | undefined,
  value: string | undefined,
): string | number | boolean | null {
  if (fieldType === undefined || value === undefined) {
    return null;
  }

  const trimmedValue = value.trim();

  switch (fieldType) {
    case CustomFieldType.Text:
      return trimmedValue;

    case CustomFieldType.Number:
      return parseFloat(trimmedValue);

    case CustomFieldType.Boolean:
      return !/^(0|false|no)$/i.test(trimmedValue);

    case CustomFieldType.Date:
    case CustomFieldType.DateAndTime: {
      const result =
        customFieldValueSchemaByType[fieldType].safeParse(trimmedValue);
      return result.success ? result.data : null;
    }

    default:
      return null;
  }
}
